/**
 * Marketing → Instagram: each episode as a posting kit — the files in the order
 * they are uploaded, and the words and link for that kind of post. The same
 * content as Marketing → Content, arranged for Instagram.
 *
 * The reel and the carousel post can be published from here, behind a confirm
 * step (PublishInstagram.tsx); stories need the link sticker and ads live in
 * Ads Manager, so those stay kits to post by hand
 * (marketing-console-target.md, "Instagram").
 */
import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, PageHeader, TabStrip, type TabItem } from '@shared-ui/components';
import type { WWEpisode } from '@shared-ui/whats-wrong/episodes.js';
import { marketingApi, type InstagramStatus, type MarketingAsset, type PublishKit } from '../../api/marketingApi';
import { AssetTile, CopyField, MissingTile } from './MarketingParts';
import { PublishInstagramDialog } from './PublishInstagram';
import { SERIES, cardC, episodesWithFiles, pick } from './episodeContent';
import './Marketing.css';

type Kit = 'reel' | 'post' | 'story' | 'ad';
/** The kits the backend can publish, by the tab they sit under. */
const PUBLISHABLE: Partial<Record<Kit, PublishKit>> = { reel: 'reel', post: 'carousel' };

const KITS: TabItem<Kit>[] = [
  { key: 'reel', label: 'Reel' },
  { key: 'post', label: 'Carousel post' },
  { key: 'story', label: 'Story' },
  { key: 'ad', label: 'Ad' },
];

interface Slot { label: string; asset?: MarketingAsset; hint: string }

function Slots({ slots }: { slots: Slot[] }) {
  return (
    <ol className="mk-slots">
      {slots.map((s, i) => (
        <li key={i}>
          {s.asset ? <AssetTile asset={s.asset} label={s.label} /> : <MissingTile label={s.label} hint={s.hint} />}
        </li>
      ))}
    </ol>
  );
}

export default function MarketingInstagram() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['marketing-assets', SERIES.id],
    queryFn: () => marketingApi.assets(SERIES.id),
    staleTime: 30 * 60 * 1000,
  });
  const [kit, setKit] = useState<Kit>('reel');
  const all = useMemo(() => data?.files ?? [], [data]);
  const episodes = useMemo(() => episodesWithFiles(all), [all]);
  const queryClient = useQueryClient();
  const ig = useQuery({ queryKey: ['instagram-status'], queryFn: marketingApi.instagramStatus, staleTime: 60 * 1000 });
  const [publishing, setPublishing] = useState<{ episode: WWEpisode; kit: PublishKit } | null>(null);
  const refreshStatus = useCallback(() => queryClient.invalidateQueries({ queryKey: ['instagram-status'] }), [queryClient]);
  const publishKit = PUBLISHABLE[kit];

  return (
    <div className="mk-page">
      <PageHeader
        title="Instagram"
        subtitle="Each episode as a posting kit: the files in upload order, then the words and link for that kind of post."
      />
      {error && <p className="mk-error">Could not load the files: {(error as Error).message}</p>}
      {isLoading && <p className="mk-sub">Loading files…</p>}
      <Connection status={ig.data} loading={ig.isLoading} error={ig.error as Error | null} />
      <TabStrip tabs={KITS} active={kit} onChange={setKit} ariaLabel="Kind of post" />

      {episodes.map(({ episode, files, links }) => {
        const name = `#${episode.id} · ${episode.title}`;
        let slots: Slot[] = [];
        let words: { label: string; text: string }[] = [];
        if (kit === 'reel') {
          slots = [
            { label: 'Reel', asset: pick(files, 'reel', 'reel'), hint: 'Render with --format=reel' },
            { label: 'Cover (Edit cover → camera roll)', asset: pick(files, 'reel', 'reel-cover'), hint: 'Rendered with the reel' },
          ];
          words = [{ label: 'Caption', text: episode.caption }];
        } else if (kit === 'post') {
          slots = [
            { label: '1 · Challenge', asset: pick(files, 'feed', 'challenge-title', 'challenge'), hint: 'Render with --format=feed --title-intro' },
            { label: '2 · Answer', asset: pick(files, 'feed', 'answer-post'), hint: 'Render with --format=feed' },
            { label: '3 · All challenges', asset: cardC(all, 'feed'), hint: 'Card C, rendered with any episode' },
          ];
          words = [{ label: 'Caption', text: episode.caption }];
        } else if (kit === 'story') {
          slots = [
            { label: '1 · Challenge', asset: pick(files, 'story', 'challenge'), hint: 'Render with --format=story' },
            { label: '2 · Answer', asset: pick(files, 'story', 'answer'), hint: 'Render with --format=story' },
            { label: '3 · All challenges (add the link sticker)', asset: cardC(all, 'story'), hint: 'Card C, story format' },
          ];
          words = [{ label: 'Link sticker', text: links.story }];
        } else {
          slots = [
            { label: 'Feed · 1 · Challenge', asset: pick(files, 'feed', 'challenge-title', 'challenge'), hint: 'Render with --format=feed --title-intro' },
            { label: 'Feed · 2 · Answer (ad)', asset: pick(files, 'feed', 'answer-ad'), hint: 'Render with --format=feed' },
            { label: 'Feed · 3 · All challenges', asset: cardC(all, 'feed'), hint: 'Card C' },
            { label: 'Reels & Stories placements', asset: pick(files, 'reel', 'reel'), hint: 'Render with --format=reel' },
          ];
          words = [
            { label: 'Primary text · Feed (carousel)', text: episode.adText },
            { label: 'Primary text · Reels & Stories', text: episode.reelAdText },
            { label: 'Website URL', text: links.ad },
          ];
        }
        return (
          <section key={episode.id} className="mk-episode">
            <header className="mk-episode-head">
              <span className="mk-num">#{episode.id}</span>
              <div><h2>{episode.title}</h2><span className="mk-sub">{episode.topic}</span></div>
              {publishKit && (
                <div className="mk-episode-actions">
                  <PostedLinks posts={(ig.data?.episodePosts?.[episode.id] ?? []).filter(p => p.type === publishKit)} />
                  <Button
                    size="sm"
                    disabled={!ig.data?.connected}
                    title={ig.data?.connected ? undefined : 'Instagram is not connected'}
                    onClick={() => setPublishing({ episode, kit: publishKit })}
                  >
                    Publish to Instagram
                  </Button>
                </div>
              )}
            </header>
            <div className="mk-episode-body">
              <Slots slots={slots} />
              <div className="mk-words" aria-label={`${name} words`}>
                {words.map(w => <CopyField key={w.label} label={w.label} text={w.text} />)}
              </div>
            </div>
          </section>
        );
      })}
      {publishing && ig.data?.connected && (
        <PublishInstagramDialog
          episode={publishing.episode}
          kit={publishing.kit}
          status={ig.data}
          assets={all}
          onClose={() => setPublishing(null)}
          onPosted={refreshStatus}
        />
      )}
    </div>
  );
}

/** The episode's posts of this kind on the account, newest first, each a link to the post. */
function PostedLinks({ posts }: { posts: NonNullable<InstagramStatus['episodePosts']>[string] }) {
  if (posts.length === 0) return <span className="mk-sub">Not posted yet</span>;
  const day = (ts?: string) => (ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');
  return (
    <span className="mk-posted">
      <Badge variant="success" size="sm">Posted{posts.length > 1 ? ` ×${posts.length}` : ''}</Badge>
      {posts.slice(0, 3).map(p => (
        <a key={p.id} href={p.permalink} target="_blank" rel="noreferrer" title="Open the post on Instagram">
          {day(p.timestamp) || 'View'} ↗
        </a>
      ))}
    </span>
  );
}

/** Which account the page posts to, or why it can't post. */
function Connection({ status, loading, error }: { status?: InstagramStatus; loading: boolean; error: Error | null }) {
  if (loading) return <p className="mk-sub">Checking the Instagram connection…</p>;
  if (error) return <p className="mk-error">Could not check the Instagram connection: {error.message}</p>;
  if (!status) return null;
  if (status.connected && status.account) {
    return (
      <p className="mk-connection">
        <Badge variant="success">Connected</Badge>
        <span>Posts go to <strong>@{status.account.username}</strong></span>
        {status.limit && <span className="mk-sub">· {status.limit.total - status.limit.used} of {status.limit.total} posts left in the 24-hour window</span>}
      </p>
    );
  }
  return (
    <p className="mk-connection">
      <Badge variant="warning">Not connected</Badge>
      <span className="mk-sub">
        {status.missing?.length
          ? `Set ${status.missing.join(' and ')} in the backend's app settings to publish from here.`
          : `Instagram refused the connection: ${status.error}`}
      </span>
    </p>
  );
}
